import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, delay, map, of } from 'rxjs';

import { CONTEXT } from '../models/context';
import { GPO_ROUTES } from '../constants/api-gpo-routes';
import { INTEGRA_ROUTES } from '../constants/api-integra-routes';
import { ConfigClientService } from './configClient.service';
import { ConfigService } from './config.service';
import { EmpresaService } from './empresa.service';
import { VerificadorPreciosAuthService } from './verificador-precios/verificador-precios-auth.service';

const VERIFICATION_DELAY_MS = 2000;

interface GpoInfoResponse {
  app?: unknown;
}

interface IntegraInfoResponse {
  version?: unknown;
}

interface PagosAuthResponse {
  token?: string;
}

@Injectable({ providedIn: 'root' })
export class ApiVerificationService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly configClientService = inject(ConfigClientService);
  private readonly empresaService = inject(EmpresaService);
  private readonly verificadorPreciosAuthService = inject(VerificadorPreciosAuthService);

  private readonly silentContext = new HttpContext().set(CONTEXT, {
    noSendHeader: true,
    ignoreError: true,
  });

  verificarSinergial(): Observable<boolean> {
    return this.empresaService.getEmpresas().pipe(
      delay(VERIFICATION_DELAY_MS),
      map((response) => !!response?.Succeeded),
      catchError(() => of(false)),
    );
  }

  verificarGpo(): Observable<boolean> {
    const baseUrl = this.configService.getAppConfig().api_gpo ?? '';
    const url = `${baseUrl}${GPO_ROUTES.INFO.VERSION}${GPO_ROUTES.INFO.PATH_BASE}`;

    return this.http.get<GpoInfoResponse>(url, { context: this.silentContext }).pipe(
      delay(VERIFICATION_DELAY_MS),
      map((response) => !!response?.app),
      catchError(() => of(false)),
    );
  }

  verificarIntegra(): Observable<boolean> {
    const baseUrl = this.configClientService.getConfig()?.api_integra ?? '';
    if (!baseUrl) {
      return of(false).pipe(delay(VERIFICATION_DELAY_MS));
    }
    const url = `${baseUrl}${INTEGRA_ROUTES.INFO.PATH_BASE}`;

    return this.http.get<IntegraInfoResponse>(url, { context: this.silentContext }).pipe(
      delay(VERIFICATION_DELAY_MS),
      map((response) => !!response?.version),
      catchError(() => of(false)),
    );
  }

  verificarPagos(): Observable<boolean> {
    const apiPagos = this.configClientService.getConfig()?.api_pagos ?? '';
    const apiPagosWithoutVersion = apiPagos.replace(/\/v\d+$/, '');
    if (!apiPagosWithoutVersion) {
      return of(false).pipe(delay(VERIFICATION_DELAY_MS));
    }

    const body = {
      client_id: this.configService.getAppConfig().api_pagos_client_id,
      secret_key: this.configClientService.getConfig()?.api_pagos_secret_key ?? '',
    };

    return this.http
      .post<PagosAuthResponse>(`${apiPagosWithoutVersion}/auth/authenticate`, body, {
        context: this.silentContext,
      })
      .pipe(
        delay(VERIFICATION_DELAY_MS),
        map((response) => !!response?.token),
        catchError(() => of(false)),
      );
  }

  /**
   * Health check del verificador de precios: comprueba que el endpoint de client
   * credentials devuelva token. Delega en `VerificadorPreciosAuthService` para no
   * duplicar acá el endpoint ni el body (deuda que sí arrastra `verificarPagos`).
   */
  verificarVerificadorPrecios(): Observable<boolean> {
    return this.verificadorPreciosAuthService.verificarToken().pipe(
      delay(VERIFICATION_DELAY_MS),
      catchError(() => of(false)),
    );
  }
}
